import * as amplitude from "@amplitude/analytics-browser";
import { Icon } from "@chakra-ui/react";
import { uniq } from "lodash";
import React, { useMemo, useState } from "react";
import { RiTextWrap } from "react-icons/ri";
import { useFormTemplates } from "src/components/Providers/FormTemplateProvider";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { useLazyRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { useSendMessage } from "src/hooks/useSendMessage";
import { FETCH_FORM_TEMPLATE_STATUSES } from "src/scenes/orgAdmin/formTemplates/graphql/queries";
import * as GQL from "src/types/graphql";
import { toTuple } from "src/types/remoteData";
import {
  BULK_CANCEL_ACTION_ITEMS,
  BULK_CREATE_ACTION_ITEMS,
} from "../graphql/mutations";
import {
  FETCH_ACTION_ITEMS,
  FETCH_FORM_STATUS,
  GET_ASSIGNED_FORMS_BY_FORM_ID,
} from "../graphql/queries";
import { SchoolFormId } from "../types";
import { NextStepFormValues, NextStepMenu } from "./NextStepMenu";

interface Props {
  formTemplateId: uuid;
  fetchSelectedFormKeys: () => Promise<SchoolFormId[]>;
}

const mapInitialValues = (
  actionItems: GQL.ActionItemFragment[] = [],
  formStatusDescription = "",
  isMixedContent = false
): NextStepFormValues => ({
  summary:
    actionItems[0]?.summary ?? (isMixedContent ? "" : formStatusDescription),
  enrollmentDescription: formStatusDescription,
  showRequired: false,
  sendMessage: false,
});

export const NextStepMenuBulk = ({
  formTemplateId,
  fetchSelectedFormKeys,
}: Props) => {
  const toast = useAvelaToast();

  const [formIds, setFormIds] = useState<uuid[]>([]);
  const [actionItems, setActionItems] = useState<
    GQL.FetchActionItems_action_item[]
  >([]);
  const { selectedNavFormTemplate } = useFormTemplates();

  const { handleSendMessage } = useSendMessage(selectedNavFormTemplate?.id);

  const [fetchFormTemplateStatuses, { remoteData: formTemplateStatusesData }] =
    useLazyRemoteDataQuery<
      GQL.FetchFormTemplateStatuses,
      GQL.FetchFormTemplateStatusesVariables
    >(FETCH_FORM_TEMPLATE_STATUSES, {
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
      variables: { form_template_id: formTemplateId },
      onError: (error) => {
        console.error(error);
      },
    });

  const [fetchFormStatus, { remoteData: formStatusData }] =
    useLazyRemoteDataQuery<GQL.FetchFormStatus, GQL.FetchFormStatusVariables>(
      FETCH_FORM_STATUS,
      {
        fetchPolicy: "no-cache",
        notifyOnNetworkStatusChange: true,
        onError: (error) => {
          console.error(error);
        },
      }
    );

  const [fetchActionItems, { remoteData: actionItemsData }] =
    useLazyRemoteDataQuery<GQL.FetchActionItems, GQL.FetchActionItemsVariables>(
      FETCH_ACTION_ITEMS,
      {
        fetchPolicy: "no-cache",
        notifyOnNetworkStatusChange: true,
        onError: (error) => {
          console.error(error);
        },
      }
    );

  const formStatusDescriptionRD = useMemo(
    () =>
      toTuple(formStatusData, formTemplateStatusesData).map(
        ([formStatuses, statusDescriptions]) => {
          const statuses = formStatuses.form.map((f) => f.status);
          const isMixed = statuses.length > 1;

          if (isMixed) {
            return { isMixedStatus: true, enrollmentStatusDescription: "" };
          }

          const statusDescription =
            statusDescriptions.form_status_description.find(
              (desc) => desc.status === statuses[0]
            )?.description;

          return {
            isMixedStatus: false,
            enrollmentStatusDescription: statusDescription ?? "",
          };
        }
      ),
    [formStatusData, formTemplateStatusesData]
  );
  const { isMixedStatus, enrollmentStatusDescription } =
    formStatusDescriptionRD.withDefault({
      isMixedStatus: false,
      enrollmentStatusDescription: "",
    });

  const [bulkCreateActionItems] = useRemoteDataMutation<
    GQL.BulkCreateActionItems,
    GQL.BulkCreateActionItemsVariables
  >(BULK_CREATE_ACTION_ITEMS);

  const [bulkCancelActionItems] = useRemoteDataMutation<
    GQL.BulkCancelActionItems,
    GQL.BulkCancelActionItemsVariables
  >(BULK_CANCEL_ACTION_ITEMS);

  const upsertActionItems = async ({
    summary,
    enrollmentDescription,
  }: NextStepFormValues) => {
    if (!summary && summary.length === 0) {
      throw new Error("A status description is required.");
    }

    const useEnrollmentDescription = summary === enrollmentDescription;

    if (useEnrollmentDescription) {
      await bulkCancelActionItems({
        variables: {
          form_ids: formIds,
        },
        refetchQueries: [FETCH_ACTION_ITEMS, GET_ASSIGNED_FORMS_BY_FORM_ID],
      });
    } else {
      await bulkCreateActionItems({
        variables: {
          form_ids: formIds,
          data: formIds.map((formId) => ({
            form_id: formId,
            summary,
            status: GQL.action_item_status_enum.Pending,
          })),
        },
        refetchQueries: [FETCH_ACTION_ITEMS, GET_ASSIGNED_FORMS_BY_FORM_ID],
      });
    }

    toast({
      description: "Status description have been updated",
      status: "info",
      duration: 2000,
    });
  };

  const handleSubmit = async (values: NextStepFormValues): Promise<boolean> => {
    amplitude.track("Confirmed bulk next step", { count: formIds.length });
    try {
      upsertActionItems(values);

      // if sendMessage is checked then navigate to new message route after a sight delay,
      // so it allows user to see status is updated
      if (values.sendMessage) {
        setTimeout(() => {
          handleSendMessage(formIds, values.summary, values.summary);
        }, 200);
      }

      amplitude.track("Completed bulk next step", { count: formIds.length });
      return true;
    } catch (e) {
      const errorName =
        e instanceof Error
          ? e.constructor.name
          : typeof e === "string"
          ? "String"
          : "Unknown";
      amplitude.track("Failed bulk next step", {
        count: formIds.length,
        error_name: errorName,
      });
      console.error(e);
      toast.error({
        title: "Something went wrong",
        description: "Please try again later or contact our team",
      });
      return false;
    }
  };

  const handleOnClick = async (evt: React.MouseEvent): Promise<boolean> => {
    const formIds = uniq(
      (await fetchSelectedFormKeys()).map((app) => app.formId)
    );
    setFormIds(formIds);
    amplitude.track("Clicked bulk next step", { count: formIds.length });
    doFetchActionItems(formIds);
    doFetchEnrollmentStatusDescription(formIds);
    return true;
  };

  const doFetchActionItems = (formIds: uuid[]) => {
    if (formIds.length !== 1 || !formIds[0]) {
      setActionItems([]);
      return;
    }

    const formId = formIds[0];

    fetchActionItems({
      variables: {
        form_id: formId,
        status: GQL.action_item_status_enum.Pending,
      },
    }).then(({ data }) => {
      setActionItems(data?.action_item ?? []);
    });
  };

  const doFetchEnrollmentStatusDescription = (formIds: uuid[]) => {
    if (!formIds?.length) return;
    fetchFormStatus({
      variables: {
        form_ids: formIds,
      },
    });
    fetchFormTemplateStatuses();
  };

  const isMixedContent = formIds.length > 1;

  const initialValues = useMemo(() => {
    return mapInitialValues(
      actionItems,
      enrollmentStatusDescription,
      isMixedContent
    );
  }, [actionItems, enrollmentStatusDescription, isMixedContent]);

  return (
    <NextStepMenu
      handleSubmit={handleSubmit}
      initialValues={initialValues}
      onClick={handleOnClick}
      placeHolderText={
        isMixedContent ? "Type to replace mixed content" : undefined
      }
      disableReset={isMixedStatus}
      placement="bottom-end"
      hasErrors={
        actionItemsData.hasError() || formStatusDescriptionRD.hasError()
      }
      leftIcon={<Icon as={RiTextWrap} />}
    />
  );
};
